import { Pricing } from 'pixai-pricing'
import { useMemo } from 'react'
import { isUserVerified } from 'src/components/Account/utils'
import { useMyInfo } from 'src/services/apollo/auth'
import type { PriceInfo } from 'src/services/apollo/generated/graphql'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'
import { useModelVersion } from 'src/stores/model'
import { userHasMembership } from 'src/utils/user'
import type { TaskSubmitFormValues } from '../BaseTaskSubmitForm'

type JslibDynamicConfig = {
  code: string
  config: Record<string, any>
  version: string
}

export type PricingTaskResult = {
  data: PriceInfo
  isValidating: boolean
  jslibVersion?: string
}

type getParmsArgType<T> = (arg0: T) => Record<string, any>

export function getPricingHeaders(
  jslibVersion?: string,
): Record<string, string> {
  return jslibVersion ? { 'X-Pricing-Script-Version': jslibVersion } : {}
}

/**
 * Hook to calculate pricing for a task.
 *
 * Hint: To improve memoization, remove prompts using `getParams` since it
 * usually has no effect on pricing.
 */
export const usePricingTask = <T extends object = TaskSubmitFormValues>(
  args: T,
  getParams: getParmsArgType<T> = args => args,
) => {
  const { data: jslibConfs, isValidating } =
    usePublicDynamicConfig<JslibDynamicConfig>('jslib', {
      revalidateIfStale: true,
      revalidateOnFocus: false,
    })
  const { data: userInfo } = useMyInfo()
  const userVerified = isUserVerified(userInfo)
  const userIsMember = userHasMembership(userInfo)

  const actualValues = getParams(args)

  const modelId =
    typeof actualValues.modelId === 'string' ? actualValues.modelId : undefined
  const version = useModelVersion(modelId)

  // to reduce cache misses
  const valuesHash = JSON.stringify(actualValues)

  const { config, version: jslibVersion } = jslibConfs ?? {}
  const memoizedPricer = useMemo(
    () =>
      new Pricing(actualValues, {
        ...config,
        shouldDiscount: userVerified || userIsMember,
        modelType: version?.modelType,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valuesHash, config, userVerified, version?.modelType],
  )

  return { data: memoizedPricer.toPriceInfo(), isValidating, jslibVersion }
}
